import queryString from 'query-string';
import type { User } from '@atlassian/jira-common-directory-v2/src/model';
import type { Filter } from '@atlassian/jira-filters-common/src/model';
import type { SharePermission } from '@atlassian/jira-shared-types';
import type { State, FiltersState } from '../../types';
import { getXsrfToken } from '../app';

export const isEmpty = (state: State): boolean => state.filters.order.length === 0;
export const getIds = (state: State): string[] => state.filters.order;
export const getTotal = (state: State): number => state.filters.total;
export const isLoading = (state: State): boolean => state.filters.isLoading;
export const isError = (state: State): boolean => state.filters.isError;
export const getName = (state: State, id: string): string | undefined =>
	state.filters.page[id]?.name;
export const getDescription = (state: State, id: string): string | undefined =>
	state.filters.page[id]?.description;
export const getOwner = (state: State, id: string): User | null | undefined =>
	state.filters.page[id]?.owner;
export const getViewUrl = (state: State, id: string): string | undefined =>
	state.filters.page[id]?.viewUrl;
export const getShares = (state: State, id: string): SharePermission[] | undefined =>
	state.filters.page[id]?.shares;
export const getEdits = (state: State, id: string): SharePermission[] | undefined =>
	state.filters.page[id]?.edits;
export const getFilter = (state: State, id: string): Partial<Filter> | undefined =>
	state.filters.page[id];
export const getIsWritable = (state: State, id: string): boolean =>
	state.filters.page[id]?.isWritable || false;
export const modalUrlFactory = (actionType: string) => (state: State, filterId: string) => {
	const xsrfToken = getXsrfToken(state);

	const parsed = queryString.stringify({
		atl_token: xsrfToken,
		returnUrl: 'ManageFilters.jspa',
		filterId,
	});

	return `/${actionType}!default.jspa?${parsed}`;
};

export const manageSubscriptionsUrl = modalUrlFactory('ViewSubscriptions');

const getFavouriteChangeContext = (state: State) => state.app.props.favouriteChangeContext;

export const getChangeFavourite = (state: State) => {
	// TODO EM-312: remove `| void` checks (e.g. `context &&`) from this and other directory pages when old provider is removed
	const context = getFavouriteChangeContext(state);
	return context && context.changeFavourite;
};

const getChangedFilter = (state: State, id: string) => {
	const context = getFavouriteChangeContext(state);
	// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
	return context && context.items.filters[id];
};

const getIsFavouriteFromState = (state: FiltersState, id: string): boolean | undefined =>
	state.page[id]?.isFavourite;
const getPopularityFromState = (state: FiltersState, id: string): number | undefined =>
	state.page[id]?.popularity;

export const isFavourite = (state: State, id: string): boolean | undefined => {
	const itemChanged = getChangedFilter(state, id);
	return itemChanged ? itemChanged.value : getIsFavouriteFromState(state.filters, id);
};

export const isPending = (state: State, id: string): boolean => {
	const item = getChangedFilter(state, id);

	return !!(item && item.pending);
};

export const getPopularity = (state: State, id: string): number | undefined => {
	const popularityInState = getPopularityFromState(state.filters, id);
	const isFavouriteInState = getIsFavouriteFromState(state.filters, id);
	const item = getChangedFilter(state, id);

	if (!item) return popularityInState;

	if (item.value && !isFavouriteInState && popularityInState) {
		return popularityInState + 1;
	}
	if (!item.value && isFavouriteInState && popularityInState) {
		return popularityInState - 1;
	}
	return popularityInState;
};
